import React, { useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';

import StartScreen from './components/StartScreen';
import Trail from './components/Trail';
import Loading from './components/Loading';
import TrailLoader from './components/TrailLoader';
import { setScreenProperties } from './store/ui';
import { getScreenProperties } from './utils/get-screen-properties';
import debounce from './utils/debounce';
import PrivacyPolicy from './components/GDPR/PrivacyPolicy';
import Modal from './components/Modal/Modal';
import Scanner from './components/Scanner';
import CodeInput from './components/CodeInput';
import { useDispatch } from 'react-redux';
import { SnapChase } from 'magical-trails-react';
import ARTest from './components/ARTest/ARTest';
import Diagnostics from './components/Diagnostics/Diagnostics';
import StorySelector from './components/StorySelector';
import RedeemInvite from './components/PlayerAccount/RedeemInvite';
import Account from './components/PlayerAccount/Account';
import InviteGate from './components/PlayerAccount/InviteGate';
import { playerAuthEnabled } from './lib/convexClient';

// Vision lab pages pull in the MediaPipe task classes directly; split them
// out so ordinary players never download that code.
const LabIndex = React.lazy(() => import('./components/Lab/LabIndex'));
const ObjectLab = React.lazy(() => import('./components/Lab/ObjectLab'));
const PersonLab = React.lazy(() => import('./components/Lab/PersonLab'));
// The page-test harness only ever matters to someone testing a page type on
// a phone; keep it out of the bundle players download.
const PageTestIndex = React.lazy(
  () => import('./components/PageTest/PageTestIndex')
);
const PageTestConfig = React.lazy(
  () => import('./components/PageTest/PageTestConfig')
);
const PageTestPlay = React.lazy(
  () => import('./components/PageTest/PageTestPlay')
);
// Phone-shell demo trails: fixtures and all, only for whoever opens /demo.
const PhoneDemoIndex = React.lazy(() =>
  import('./components/PhoneDemo/PhoneDemo').then((module) => ({
    default: module.PhoneDemoIndex,
  }))
);
const PhoneDemoPlay = React.lazy(() =>
  import('./components/PhoneDemo/PhoneDemo').then((module) => ({
    default: module.PhoneDemoPlay,
  }))
);
const LabFallback = (
  <div className="fixed inset-0 flex items-center justify-center bg-slate-900 text-white">
    Loading lab…
  </div>
);

/** Shown on account routes when the Convex env pair is not configured. */
const AccountsUnavailable: React.FC = () => (
  <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
    <div className="bg-mt-surface flex w-72 flex-col gap-3 rounded-2xl p-6 text-center shadow-2xl">
      <h1 className="text-mt-ink-soft text-xl font-bold tracking-tight">
        Accounts are unavailable
      </h1>
      <p className="text-mt-ink-muted text-sm">
        Player accounts aren&apos;t enabled in this environment. Open trails
        play without an account.
      </p>
    </div>
  </div>
);

const AppRouter: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setScreenProperties(getScreenProperties()));
    const handleResize = debounce(() =>
      dispatch(setScreenProperties(getScreenProperties()))
    );
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  return (
    <Router>
      <StorySelector />
      {/* Auth-dependent screens mount ONLY when the Convex provider exists
          (playerAuthEnabled ⇒ App wrapped the tree in
          ConvexBetterAuthProvider) — their Convex hooks would throw
          otherwise. */}
      {playerAuthEnabled && <InviteGate />}
      <main id="main-content">
        <Switch>
          <Route path="/" exact>
            <StartScreen />
          </Route>
          <Route path="/privacy-policy" exact>
            <PrivacyPolicy />
          </Route>
          <Route path="/redeem/:token">
            {playerAuthEnabled ? <RedeemInvite /> : <AccountsUnavailable />}
          </Route>
          <Route path="/account" exact>
            {playerAuthEnabled ? <Account /> : <AccountsUnavailable />}
          </Route>
          <Route path="/trail/:trailId">
            <Trail />
          </Route>
          {/* AR isolation harness — see components/ARTest/ARTest.tsx. */}
          <Route path="/ar-test" exact>
            <ARTest />
          </Route>
          {/* Field self-test — see components/Diagnostics/Diagnostics.tsx. */}
          <Route path="/diagnostics" exact>
            <Diagnostics />
          </Route>
          {/* Camera capability tests — see components/Lab/. */}
          <Route path="/lab" exact>
            <React.Suspense fallback={LabFallback}>
              <LabIndex />
            </React.Suspense>
          </Route>
          <Route path="/lab/objects" exact>
            <React.Suspense fallback={LabFallback}>
              <ObjectLab />
            </React.Suspense>
          </Route>
          <Route path="/lab/person" exact>
            <React.Suspense fallback={LabFallback}>
              <PersonLab />
            </React.Suspense>
          </Route>
          {/* Page harness — see components/PageTest/. */}
          <Route path={['/page-test', '/test-page']} exact>
            <React.Suspense fallback={LabFallback}>
              <PageTestIndex />
            </React.Suspense>
          </Route>
          <Route path={['/page-test/:type', '/test-page/:type']} exact>
            <React.Suspense fallback={LabFallback}>
              <PageTestConfig />
            </React.Suspense>
          </Route>
          <Route
            path={['/page-test/:type/play', '/test-page/:type/play']}
            exact
          >
            <React.Suspense fallback={LabFallback}>
              <PageTestPlay />
            </React.Suspense>
          </Route>
          {/* Phone-shell demos — see components/PhoneDemo/. */}
          <Route path="/demo" exact>
            <React.Suspense fallback={LabFallback}>
              <PhoneDemoIndex />
            </React.Suspense>
          </Route>
          <Route path="/demo/:id" exact>
            <React.Suspense fallback={LabFallback}>
              <PhoneDemoPlay />
            </React.Suspense>
          </Route>
          <Route path="/loading">
            <Loading />
          </Route>
          <Route path={`/scan`}>
            <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
              <Modal
                isOpen
                handleClose={() => {
                  history.back();
                }}
              >
                <Scanner
                  closeModal={() => {
                    history.back();
                  }}
                />
              </Modal>
            </div>
          </Route>
          <Route path={`/enter-code`}>
            <div className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat">
              <Modal
                isOpen
                handleClose={() => {
                  history.back();
                }}
              >
                <CodeInput />
              </Modal>
            </div>
          </Route>
          <Route path={'/snap-chase'}>
            <SnapChase
              page={{
                type: 'snapChase',
                character: {
                  imageUrl:
                    'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/user_uploads/yule_cat.png',
                  fps: 12,
                  frames: 2,
                  frameSize: [512, 512],
                },
                panoImageUrl:
                  'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/user_uploads/full-seamless-spherical-panorama.jpg',
                backgroundImage:
                  'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/christmas_creature_chronicals/background_1.jpg',
                instructionsImage:
                  'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/christmas_creature_chronicals/blaze_explains_1.jpg',
                instructions:
                  '<p>Righto mates, we are in the perfect spot. Your Creature Camera is ready. Let’s see if we can catch a beauty.</p>',
                wrongAnswerText:
                  '<p>That creature slipped right out of frame. Sneaky little thing.</p>',
                completeText:
                  '<p>Yes. That is the stuff. Blaze Bramblewhip approves.</p>',
                completeImage:
                  'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/christmas_creature_chronicals/blaze_happy_1.jpg',
                wrongAnswerImage:
                  'https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/christmas_creature_chronicals/blaze_unhappy_1.jpg',
              }}
              trailEvent={() => {}}
              editorMode={false}
            />
          </Route>
          {/* <Route path={'/snap-chase-lite'}>
          <SnapChaseLite
            panoImageUrl="https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/user_uploads/full-seamless-spherical-panorama.jpg"
            distanceMeters={3}
            useDeviceMotion
            allowDragAim
          />
        </Route> */}

          {/*
        <Route path="/three">
          <ThreeJS
            imageUrl="https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/images/christmas_2024/Suprised%20Santa.jpg"
            depthMapUrl="https://hyvniifjflduwvowavgd.supabase.co/storage/v1/object/public/media/images/christmas_2024/Suprised%20Santa%20Depth.jpg"
            particles="snow"
            displacementScale={0.3}
          />
        </Route> */}
          <Route path="/:id">
            <TrailLoader />
          </Route>
        </Switch>
      </main>
    </Router>
  );
};

export default AppRouter;
